import { DomainMapping } from '../../types/domain-mapping';
import { Domain } from '../../types/domain';
import { StoryblokResourceMappingEntry } from '../../types/storyblok-resource-mapping-entry';
import { StringService } from '../string';
import { Country, CountryEnum } from '../../types/country';
import { getAppConfig } from '../../utils/get-app-config';
import { JentisConfig } from '../../types/configuration';
import { EnvironmentContext } from '../../types/environement';

interface DomainMappingEntry extends StoryblokResourceMappingEntry {
  domain: Domain;
}

export const DomainService = {
  getCurrentDomain(): Domain {
    return process.env.GATSBY_DOMAIN as Domain;
  },

  getCurrentEnvironmentContext(): EnvironmentContext {
    return process.env.GATSBY_ENV as EnvironmentContext;
  },

  getDomainMapping(): DomainMapping {
    return getAppConfig().domainMapping;
  },

  getDomainToUrlMapping(): Partial<Record<Domain, string>> {
    return getAppConfig().domainToUrlMapping;
  },

  getRetailDomainCountryMap(): Partial<Record<Domain, CountryEnum>> {
    return getAppConfig().retailDomainCountryMap;
  },

  getDomainSpecificJentisConfig(): JentisConfig {
    return getAppConfig().domainToJentisConfigMap[this.getCurrentDomain()];
  },

  getDomainSpecificOneTrustKey(): string {
    return getAppConfig().domainToOnetrustKeyMap[this.getCurrentDomain()];
  },

  getDomainSpecificAbsoluteUrl(url: string): string {
    const domainToUrlMapping = this.getDomainToUrlMapping();
    return this.removeTrailingSlash(`${domainToUrlMapping[this.getCurrentDomain()]}${url}`);
  },

  getDomainSpecificEmApplicationToken(): string {
    return getAppConfig().domainToEmApplicationTokenMap === undefined
      ? '' // We do not have tokens for DEV environment, so we return an empty string
      : getAppConfig().domainToEmApplicationTokenMap[this.getCurrentDomain()][this.getCurrentEnvironmentContext()];
  },

  isRetailDomain(domain: Domain): boolean {
    const { retailDomains } = getAppConfig();
    return retailDomains.includes(domain);
  },

  isInstitutionalDomain(domain: Domain): boolean {
    const { institutionalDomains } = getAppConfig();
    return institutionalDomains.includes(domain);
  },

  isRebranded(country: Country): boolean {
    const { rebrandedCountries } = getAppConfig();
    return rebrandedCountries.includes(country as CountryEnum);
  },

  getDomainByUrl(url: string): Domain {
    const allDomains = Object.values(Domain) as Domain[];
    const fallbackDomain = Domain.INSTI;

    const domainToUrlMapping = this.getDomainToUrlMapping();

    return allDomains.find((domain: Domain) => url.startsWith(domainToUrlMapping[domain])) || fallbackDomain;
  },

  createDomainSpecificLink(fullSlug: string, currentDomain: Domain): string {
    const sanitizedSlug = fullSlug.includes('home') ? fullSlug.replace('home', '') : fullSlug;
    const normalizedSlug = sanitizedSlug.startsWith('/') ? sanitizedSlug.substring(1) : sanitizedSlug;

    if (!StringService.hasCountryLocaleInfo(normalizedSlug)) {
      return this.removeTrailingSlash(`/${normalizedSlug}`);
    }

    const bestMatch: DomainMappingEntry | null = this.findBestMatch(normalizedSlug, currentDomain);

    if (!bestMatch) {
      // No match found; ensure leading slash
      return this.removeTrailingSlash(`/${normalizedSlug}`);
    }

    const { domain: matchedDomain, sbResource: matchedResource, buildAs: matchedBuildAs } = bestMatch;
    const transformedLink = this.transformLink(normalizedSlug, matchedResource, matchedBuildAs);

    return this.removeTrailingSlash(this.constructFinalLink(transformedLink, matchedDomain, currentDomain));
  },

  findBestMatch(sbFullSlug: string, currentDomain: Domain): DomainMappingEntry | null {
    const sbFolders = sbFullSlug.split('/');
    const domainMapping = this.getDomainMapping();

    const matchingDomainMappings: DomainMappingEntry[] = Object.entries(domainMapping)
      .flatMap(
        ([domain, mappings]: [string, StoryblokResourceMappingEntry[]]) =>
          this.mapSbResourcesToDomainMappingEntries(mappings, domain) as DomainMappingEntry[],
      )
      .filter((domainMappingEntry: DomainMappingEntry) => this.isResourceMatch({ sbFolders, domainMappingEntry }))
      .sort((a, b) => b.sbResource.length - a.sbResource.length);

    return matchingDomainMappings.find(({ domain }) => domain === currentDomain) || matchingDomainMappings[0];
  },

  isResourceMatch({ domainMappingEntry, sbFolders }: { domainMappingEntry: DomainMappingEntry; sbFolders: string[] }): boolean {
    const { sbResource, exclude } = domainMappingEntry;

    const entryFolders = sbResource.split('/');

    const isMatchingSbResource = entryFolders.every((entryFolder: string, index: number) => sbFolders[index] === entryFolder);

    // if it is not a matching resource, then it is simply not a match
    if (!isMatchingSbResource) {
      return false;
    }

    // if it is a matching resource, and it does not have any info about excluded folders, then it is a match
    if (!exclude) {
      return true;
    }

    // if it is a matching resource, then we should check if the current resource is still excluded
    // if the current resource is excluded, then it is not a match
    // otherwise it is a match
    return !exclude.find((excludedResource) => {
      const excludedResourceFolders = excludedResource.split('/');

      return excludedResourceFolders.every((excludedEntryFolder: string, index: number) => sbFolders[index] === excludedEntryFolder);
    });
  },

  mapSbResourcesToDomainMappingEntries(sbResourceMappingEntries: StoryblokResourceMappingEntry[], domain: Domain): DomainMappingEntry[] {
    return sbResourceMappingEntries.map(
      ({ sbResource, buildAs }) =>
        ({
          domain: domain as Domain,
          sbResource,
          buildAs,
        }) as DomainMappingEntry,
    );
  },

  transformLink(link: string, matchedResource: string, buildAs: string): string {
    return link.replace(matchedResource, buildAs).replace(/^\/+/, '');
  },

  removeTrailingSlash(link: string): string {
    return link.length > 1 && link.endsWith('/') ? link.slice(0, -1) : link;
  },

  constructFinalLink(transformedLink: string, matchedDomain: Domain, currentDomain: Domain): string {
    const domainToUrlMapping = this.getDomainToUrlMapping();
    if (matchedDomain !== currentDomain) {
      // Different domain: prepend full domain URL
      return `${domainToUrlMapping[matchedDomain]}/${transformedLink}`;
    }

    // Same domain: return as relative link with leading slash
    return `/${transformedLink}`;
  },

  /**
   * Creates a domain-specific path by finding the longest matching sbResource
   * and replacing it with its corresponding buildAs value.
   *
   * @param {string} fullSlug - The full slug of the entry.
   * @param domainMappings
   * @returns {string} - The updated domain-specific path.
   */
  createDomainSpecificPath(fullSlug: string, domainMappings: StoryblokResourceMappingEntry[]): string {
    // Remove 'home' from the full slug if present
    const sanitizedSlug = fullSlug.includes('home') ? fullSlug.replace('home', '') : fullSlug;

    // Sort domainMapping by sbResource length (longest match first)
    const sortedMapping = domainMappings.sort((a, b) => b.sbResource.length - a.sbResource.length);

    // Find the longest matching sbResource
    const matchingMapping = sortedMapping.find(({ sbResource }) => sanitizedSlug.startsWith(sbResource));

    // Generate the domain-specific path if a matching mapping is found
    return matchingMapping ? sanitizedSlug.replace(matchingMapping.sbResource, matchingMapping.buildAs) : sanitizedSlug;
  },
};
